import React, { useState, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  LinearProgress,
  Stack,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Divider,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageIcon from "@mui/icons-material/Image";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import html2canvas from "html2canvas";

import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import { ContactsThankYouMessage } from "./ContactsThankYouMessage";

export interface ContactItem {
  id: string;
  name: string;
  phone: string;
  email: string;
  source: string;
}

interface BulkWhatsAppModalProps {
  open: boolean;
  onClose: () => void;
  contacts: ContactItem[];
  deceasedName?: string;
  streamLink?: string;
  onSent?: (sentContactIds: string[]) => void;
}

const isRealPhone = (value?: string | null): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "n/a" &&
    normalized !== "na" &&
    normalized !== "undefined"
  );
};

const compileMessage = (
  templateStr: string,
  name: string,
  deceasedName: string,
  streamLink: string,
): string => {
  return templateStr
    .replace(/\{name\}/gi, name || "Friend")
    .replace(/\{deceasedName\}/gi, deceasedName || "our beloved")
    .replace(/\{streamLink\}/gi, streamLink || "");
};

const initialsOf = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

// Deterministic-ish avatar color from a string, keeps the list visually
// distinguishable without random re-renders.
const avatarPalette = [
  "#0f766e",
  "#7c3aed",
  "#b45309",
  "#0369a1",
  "#be123c",
  "#4d7c0f",
];
const avatarColorFor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

export const BulkWhatsAppModal: React.FC<BulkWhatsAppModalProps> = ({
  open,
  onClose,
  contacts,
  deceasedName = "",
  streamLink = "",
  onSent,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const defaultTemplate =
    "Hi {name},I'm Tawanda from Muzukuru Funeral Streaming. \n\nThank you for your Memory message for  {deceasedName} 🙏 I've added it to the Memory Book ,your words will mean a lot to the family.\n\nYou can watch the stream here: {streamLink}";

  const [messageTemplate, setMessageTemplate] = useState(defaultTemplate);
  const [sendFormat, setSendFormat] = useState<"image" | "text">("image");
  const [isSending, setIsSending] = useState(false);
  const [statusStep, setStatusStep] = useState<string>("");
  const [skippedOpen, setSkippedOpen] = useState(false);
  const [results, setResults] = useState<{
    sent: number;
    failed: number;
    details?: Array<{
      phone: string;
      name: string;
      status: string;
      error?: string;
    }>;
  } | null>(null);

  const { validContacts, invalidContacts } = useMemo(() => {
    const valid: ContactItem[] = [];
    const invalid: ContactItem[] = [];
    contacts.forEach((c) => {
      if (isRealPhone(c.phone)) {
        valid.push(c);
      } else {
        invalid.push(c);
      }
    });
    return { validContacts: valid, invalidContacts: invalid };
  }, [contacts]);

  const previewContact = validContacts[0] || contacts[0];
  const previewMessage = useMemo(() => {
    if (!previewContact) return "";
    return compileMessage(
      messageTemplate,
      previewContact.name,
      deceasedName,
      streamLink,
    );
  }, [messageTemplate, previewContact, deceasedName, streamLink]);

  const generateCardBase64 = async (): Promise<string | null> => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        width: 1080,
        height: 1080,
        scale: 1,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      console.error("Failed to capture graphic card:", err);
      return null;
    }
  };

  const handleSendBulk = async () => {
    if (validContacts.length === 0) {
      showErrorToast("No selected contacts have valid phone numbers.");
      return;
    }

    setIsSending(true);
    setResults(null);

    let imageB64: string | null = null;
    if (sendFormat === "image") {
      setStatusStep("Generating Thank-You Graphic Card...");
      imageB64 = await generateCardBase64();
      if (!imageB64) {
        showErrorToast("Couldn't generate graphic card. Sending as text only.");
      }
    }

    setStatusStep("Sending messages via Meta Business API...");

    const recipientsPayload = validContacts.map((c) => ({
      name: c.name,
      phone: c.phone,
      message: compileMessage(
        messageTemplate,
        c.name,
        deceasedName,
        streamLink,
      ),
    }));

    try {
      const response = await apiService.post<{
        ok: boolean;
        sent: number;
        failed: number;
        results: Array<{
          phone: string;
          name: string;
          status: string;
          error?: string;
        }>;
      }>("/api/whatsapp/send-bulk/", {
        recipients: recipientsPayload,
        provider: "meta",
        image_b64: imageB64 || undefined,
      });

      if (response.data.ok) {
        setResults({
          sent: response.data.sent,
          failed: response.data.failed,
          details: response.data.results,
        });

        if (response.data.sent > 0) {
          showSuccessToast(
            `Successfully sent ${response.data.sent} WhatsApp message(s) via Meta API!`,
          );
          if (onSent) {
            const successfulPhones = new Set(
              (response.data.results || [])
                .filter((r) => r.status === "sent")
                .map((r) => r.phone),
            );
            const sentIds = validContacts
              .filter((c) => successfulPhones.has(c.phone))
              .map((c) => c.id);
            onSent(sentIds);
          }
        } else {
          showErrorToast("Failed to send bulk WhatsApp messages.");
        }
      } else {
        showErrorToast("Failed to initiate bulk WhatsApp send.");
      }
    } catch (err: unknown) {
      console.error("Bulk WhatsApp error:", err);
      showErrorToast(
        "An error occurred while sending bulk WhatsApp messages via Meta API.",
      );
    } finally {
      setIsSending(false);
      setStatusStep("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon sx={{ color: "#25D366", fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Send Bulk WhatsApp Messages
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {validContacts.length + invalidContacts.length} contact(s)
                selected via Meta Business API
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSending}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {/* Hidden full-resolution DOM node for html2canvas rendering */}
        <Box
          sx={{
            width: 0,
            height: 0,
            overflow: "hidden",
            position: "absolute",
            left: -9999,
          }}
        >
          <Box ref={cardRef} sx={{ width: 1080, height: 1080 }}>
            <ContactsThankYouMessage deceasedName={deceasedName} />
          </Box>
        </Box>

        {/* Recipients — who's actually getting this */}
        <Box mb={2.5}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Recipients
          </Typography>
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {validContacts.map((c, idx) => (
                <Box
                  key={c.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderBottom:
                      idx < validContacts.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 13,
                      fontWeight: 700,
                      bgcolor: avatarColorFor(c.id || c.name),
                    }}
                  >
                    {initialsOf(c.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ color: "#1e293b" }}
                    >
                      {c.name || "Unnamed contact"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.phone}
                    </Typography>
                  </Box>
                  {c.source && (
                    <Chip
                      label={c.source}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        borderColor: "#e2e8f0",
                        color: "text.secondary",
                      }}
                    />
                  )}
                  <CheckCircleIcon sx={{ color: "#25D366", fontSize: 18 }} />
                </Box>
              ))}
              {validContacts.length === 0 && (
                <Box sx={{ px: 1.5, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    None of the selected contacts have a valid phone number.
                  </Typography>
                </Box>
              )}
            </Box>

            {invalidContacts.length > 0 && (
              <>
                <Divider />
                <Box
                  onClick={() => setSkippedOpen((v) => !v)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: "#fffbeb",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneDisabledIcon
                      sx={{ fontSize: 16, color: "#b45309" }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#92400e" }}
                    >
                      {invalidContacts.length} contact(s) will be skipped (no
                      phone number)
                    </Typography>
                  </Box>
                  {skippedOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: "#92400e" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: "#92400e" }} />
                  )}
                </Box>
                <Collapse in={skippedOpen}>
                  <Box sx={{ maxHeight: 140, overflowY: "auto" }}>
                    {invalidContacts.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 1.5,
                          py: 0.75,
                          borderTop: "1px solid #fef3c7",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: "#e2e8f0",
                            color: "#64748b",
                          }}
                        >
                          {initialsOf(c.name)}
                        </Avatar>
                        <Typography
                          variant="caption"
                          sx={{ color: "#78716c", flex: 1 }}
                          noWrap
                        >
                          {c.name || "Unnamed contact"}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          no phone on file
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        </Box>

        {/* Format Selector */}
        <Box mb={2.5}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Message Type
          </Typography>
          <ToggleButtonGroup
            value={sendFormat}
            exclusive
            onChange={(_, val) => val && setSendFormat(val)}
            disabled={isSending}
            fullWidth
            size="small"
          >
            <ToggleButton
              value="image"
              sx={{
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px!important",
                "&.Mui-selected": {
                  bgcolor: "rgba(37, 211, 102, 0.12)",
                  borderColor: "#25D366",
                  color: "#0f766e",
                },
              }}
            >
              <ImageIcon sx={{ mr: 1, fontSize: 20 }} />
              Graphic Card + Message Text
            </ToggleButton>
            <ToggleButton
              value="text"
              sx={{
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px!important",
                "&.Mui-selected": {
                  bgcolor: "rgba(37, 211, 102, 0.12)",
                  borderColor: "#25D366",
                  color: "#0f766e",
                },
              }}
            >
              <TextFieldsIcon sx={{ mr: 1, fontSize: 20 }} />
              Text Only
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Template Input */}
        <Typography variant="subtitle2" fontWeight={600} mb={1}>
          Message Text {sendFormat === "image" ? "(Image Caption)" : ""}
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={messageTemplate}
          onChange={(e) => setMessageTemplate(e.target.value)}
          placeholder="Type message..."
          disabled={isSending}
          variant="outlined"
          sx={{
            mb: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "14px",
            },
          }}
        />

        <Box display="flex" alignItems="center" gap={0.5} mb={2.5}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Use placeholders: <strong>{"{name}"}</strong>,{" "}
            <strong>{"{deceasedName}"}</strong>,{" "}
            <strong>{"{streamLink}"}</strong>
          </Typography>
        </Box>

        {/* Live Preview */}
        {previewContact && (
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              display="block"
              mb={1}
            >
              Preview for {previewContact.name}:
            </Typography>

            {sendFormat === "image" && (
              <Box
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                  width: "100%",
                  aspectRatio: "1 / 1",
                  position: "relative",
                  bgcolor: "#fff",
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    transform: "scale(0.333)",
                    transformOrigin: "top left",
                    position: "absolute",
                  }}
                >
                  <ContactsThankYouMessage deceasedName={deceasedName} />
                </Box>
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: "#1e293b",
                fontSize: "13px",
              }}
            >
              {previewMessage}
            </Typography>
          </Box>
        )}

        {/* Sending state */}
        {isSending && (
          <Box sx={{ my: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={1}
              textAlign="center"
            >
              {statusStep ||
                "Sending messages via Meta WhatsApp Business API..."}
            </Typography>
            <LinearProgress sx={{ borderRadius: 2, height: 8 }} />
          </Box>
        )}

        {/* Results summary */}
        {results && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: "12px",
              bgcolor: results.failed === 0 ? "#f0fdf4" : "#fff1f2",
              border: `1px solid ${results.failed === 0 ? "#bbf7d0" : "#fecdd3"}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              {results.failed === 0 ? (
                <CheckCircleIcon sx={{ color: "#16a34a" }} />
              ) : (
                <ErrorIcon sx={{ color: "#e11d48" }} />
              )}
              <Typography variant="subtitle2" fontWeight={700}>
                Sent: {results.sent} | Failed: {results.failed}
              </Typography>
            </Stack>
            {results.details && results.details.length > 0 && (
              <Box sx={{ maxHeight: 120, overflowY: "auto", pt: 1 }}>
                {results.details.map((d, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    display="block"
                    color={d.status === "sent" ? "success.main" : "error.main"}
                  >
                    • {d.name || d.phone}:{" "}
                    {d.status === "sent"
                      ? "Sent successfully"
                      : d.error || "Failed"}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1.5 }}>
        <Button onClick={onClose} disabled={isSending} color="inherit">
          {results ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSendBulk}
          disabled={isSending || validContacts.length === 0}
          startIcon={<WhatsAppIcon />}
          sx={{
            bgcolor: "#25D366",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 3,
            "&:hover": {
              bgcolor: "#1cbd59",
            },
            "&.Mui-disabled": {
              bgcolor: "#a8e6c1",
              color: "#fff",
            },
          }}
        >
          {isSending
            ? "Sending..."
            : `Send to ${validContacts.length} Contact(s) via Meta API`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
